// hooks/codes/useAutoSaveSynthesis.js

import { useState, useRef, useCallback, useEffect } from 'react';
import { useUpdateCodeSynthesis } from './mutations/useUpdateCodeSynthesis';

const CHANGE_COUNT_AUTO_SAVE = 100;

/**
 * Custom hook for managing auto-saving synthesis text with optimistic updates
 * @param {string} codeId - The code ID
 * @param {string} initialSynthesis - Initial synthesis text from code.synthesis
 * @returns {Object} Hook interface with handlers and state
 */
export const useAutoSaveSynthesis = (codeId, initialSynthesis) => {
  const [textAreaFocused, setTextAreaFocused] = useState(false);
  const [synthesisText, setSynthesisText] = useState(initialSynthesis || "");
  const countRef = useRef(1);
  const updateSynthesisMutation = useUpdateCodeSynthesis();

  // Sync local state with cache updates (only when not focused)
  useEffect(() => {
    if (!textAreaFocused) {
      setSynthesisText(initialSynthesis || "");
    }
  }, [initialSynthesis, textAreaFocused]);

  const saveSynthesisText = useCallback((text) => {
    if (codeId && text !== initialSynthesis) {
      updateSynthesisMutation.mutate({
        codeId: codeId,
        synthesis: text
      });
    }
  }, [codeId, initialSynthesis, updateSynthesisMutation]);

  const handleSynthesisChange = useCallback((event) => {
    setSynthesisText(event.target.value);

    countRef.current += 1;
    if (countRef.current % CHANGE_COUNT_AUTO_SAVE === 0) {
      saveSynthesisText(event.target.value);
    }
  }, [saveSynthesisText]);

  const handleTextAreaFocus = useCallback(() => {
    setTextAreaFocused(true);
  }, []);

  const handleTextAreaBlur = useCallback(() => {
    setTextAreaFocused(false);
    saveSynthesisText(synthesisText);
  }, [synthesisText, saveSynthesisText]);

  // Always use synthesisText as source of truth
  const displayText = synthesisText;

  return {
    displayText,
    textAreaFocused,
    handleSynthesisChange,
    handleTextAreaFocus,
    handleTextAreaBlur,
    isLoading: updateSynthesisMutation.isPending,
    error: updateSynthesisMutation.error
  };
};